'use strict'

const AVAIL_SORT_ORDER = {
  PRIO_LIST: Symbol.for(-1), // use retailer priority list
  DEFAULT : Symbol.for(0), // by retailer name, "alphabetically" (i.e. unicode codepoint)
  RANDOM: Symbol.for(1),
  PRICE: Symbol.for(2)
}

// Equivalent units table. Each line represents variations of the same unit.
const UNITS = [
  ['oz', 'ounce', 'ounces'],
  ['kg', 'kgs'],
  ['g', 'grm', 'gm']
]

// Class of trade — static maps
const COT_KNOWN = ['store', 'restaurant', 'bar', 'entertainment', 'other']
const COT_ICON_MAP = {
  store: 'shop',
  restaurant: 'restaurant',
  entertainment: 'cinema',
  bar: 'glass',
  other: 'ellipsis'
}

// Just a bunch of miscellaneous helper functions
class Utils {
  static COT_ON_PREMISE = ['bar', 'restaurant', 'entertainment']
  static COT_OFF_PREMISE = ['store', 'other']
  static COT_LAYOUTS = ['icon', 'text', 'icon+text']

  // returns location from lat/lng coordinates.
  // if cb is specified, executes it as a function. Otherwise returns a promise.
  static getReverseGeocoding(lng, lat, countries = [], language = null, source = '', auto, cb){
    let qs = 'access_token=pk.eyJ1Ijoic3dhdmVuIiwiYSI6ImNqbTd1dXBlcjFhejMzcHBvajVlYjFjMXcifQ.ljVSrON2CjvnSj38Nxa9RA'

    if (countries.length > 0)
      qs+= `&country=${typeof countries === 'string' ? countries : countries.join(',')}`
    if (language)
      qs+= `&language=${language}`

    return new Promise(async (resolve, reject) => {
      try{
        const res = await fetch(`https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?${qs}`),
              data = await res.json()

        if (res.status >= 400)
          throw new Error(data.message)

        if (data.features.length === 0)
          resolve([null, null])

        resolve([null, Utils.parseFeature(data.features[0], source, auto)])
      }
      catch(ex){
        console.error(ex)
        Tracker.log('error', {msg: ex.message, stack: ex.stack})
        resolve(['Mapbox reverse geocoding failure'])
      }
      finally {
        // track mapbox usage
        Tracker.log('mapbox-request', { api: 'geocode', query: `${lng},${lat}` })
      }
    })
    .then(([err, data]) => {
      if (cb)
        cb(err, data)
      else
        return Promise.resolve(err ? null : data) // always resolves, but with null location if an error occurred
    })
  }

  // queries Mapbox and returns results. Executes a callback or returns a promise if not specified.
  // @query (string): search query
  // @countries (array of string): countries the search is restricted to
  // @locale (string): language of the search query and of the results
  // @limit (integer): max number of results returned
  // @cb (function): callback to execute with results.
  static queryMapbox(query, countries, locale, limit = 5, cb){
    const qs = `country=${countries.join(',')}&language=${locale}&limit=${limit}&access_token=pk.eyJ1Ijoic3dhdmVuIiwiYSI6ImNqbTd1dXBlcjFhejMzcHBvajVlYjFjMXcifQ.ljVSrON2CjvnSj38Nxa9RA`

    return new Promise(async (resolve, reject) => {
      try{
        const res = await fetch(`https://api.mapbox.com/geocoding/v5/mapbox.places/${query}.json?${qs}`),
              data = await res.json()
        resolve([null, data.features])
      }
      catch(ex){
        console.error(ex)
        Tracker.log('error', {msg: ex.message, stack: ex.stack})
        resolve(['Mapbox query failure'])
      }
      finally {
        // track mapbox usage
        Tracker.log('mapbox-request', { api: 'geocode', query })
      }
    })
    .then(([err, data]) => {
      if (cb)
        cb(err, data)
      else if (err)
        return Promise.reject(err)
      else
        return Promise.resolve(data)
    })
  }

  // parse results from reverse geocoding request into a location object
  static parseFeature(feat, source = '', auto){
    const place = {
      address: '',
      city: '',
      zipCode: '',
      region: '',
      country: '',
      latitude: 0,
      longitude: 0,
      boundingBox: null,
      source: source,
      auto: !!auto,
      type: ''
    }

    if (!feat)
      return place

    if (feat.place_type.includes('postcode'))
      place.zipCode = feat.text
    if (feat.place_type.includes('country') && feat.properties.short_code)
      place.country = feat.properties.short_code.toUpperCase()

    if (feat.context){
      feat.context.forEach((context) => {
        let type = context.id.split('.')[0]
        switch (type) {
          case 'postcode':
            if (!place.zipCode)
              place.zipCode = context.text
            break
          case 'place':
            place.city = context.text
            break
          case 'region':
            if (context.short_code){
              place.region = context.short_code.toUpperCase()

              if (!place.country) // use region as country if it's not defined
                place.country = place.region
            }
            break
          case 'country':
            if (context.short_code)
              place.country = context.short_code.toUpperCase()
            break
        }
      })
    }

    // If we don't have a city and it's a 'place' we can assume the city is the place's name
    if (!place.city && feat.place_type.includes('place')){
      place.city = feat.text
    }

    place.longitude = feat.center[0]
    place.latitude = feat.center[1]
    place.address = feat.place_name

    // use the most relevant place type
    if (feat.place_type && feat.place_type.length > 0)
      place.type = feat.place_type[0]

    // if place is a region, take its code from properties
    if (place.type === 'region' && feat.properties.short_code)
      place.region = feat.properties.short_code.toUpperCase()

    if (feat.bbox)
      place.boundingBox = feat.bbox

    if (place.type === 'country'){
      const bbox = Utils.rewriteBoundingBox(place.country)
      if (bbox)
        place.boundingBox = bbox
    }

    // remove country prefix from region code
    if (place.region && place.country)
      place.region = place.region.replace(new RegExp(`^${place.country}-`, 'i'), '')

    return place
  }

  // Mapbox returns incorrect boundingBoxes for some countries. Replace them with hardcoded values.
  static rewriteBoundingBox(country){
    let bbox
    switch(country){
      case 'FR': bbox = [-5.238938, 41.362375, 9.9561494, 51.184074]
        break
    }
    return bbox
  }

  // Returns the distance in meters between 2 coordinates.
  // loc1, loc2: objects|arrays. If objects, they must have latitude (or lat) and longitude (or lng) properties.
  // If arrays, index 0 is longitude, index 1 is latitude.
  // loc1 can be an object and loc2 an array (or vice-versa)
  static getHaversineDistance(loc1, loc2){
    const earthDiameter = 12742 * 1000, // in metres
          lng1 = Utils._toRadians(loc1.longitude || loc1.lng || loc1[0] || 0),
          lat1 = Utils._toRadians(loc1.latitude || loc1.lat || loc1[1] || 0),
          lng2 = Utils._toRadians(loc2.longitude || loc2.lng || loc2[0] || 0),
          lat2 = Utils._toRadians(loc2.latitude || loc2.lat || loc2[1] || 0),

          havLat = Math.sin((lat2 - lat1) / 2 ) ** 2,
          havLng = Math.sin((lng2 - lng1) / 2 ) ** 2,
          h = Math.min(havLat + havLng * Math.cos(lat1) * Math.cos(lat2), 1) // h cannot be > 1

    return earthDiameter * Math.asin(Math.sqrt(h))
  }

  // convert degrees to radians
  static _toRadians(val){
    return val * Math.PI / 180
  }

  static uuid(a){
    // https://gist.github.com/jed/982883
    return a?(a^Math.random()*16>>a/4).toString(16):([1e7]+-1e3+-4e3+-8e3+-1e11).replace(/[018]/g, Utils.uuid)
  }

  // Replace region pattern to point to the right AWS endpoint
  static changeAWSEndpoint(url, region){
    if (!region) // region can be empty if worldwide
      return url.replace('{{region}}-', '')
    else
      return url.replace('{{region}}', region)
  }

  // Deep copy of the given object
  static clone(obj, ignoreFunctions = false){
    if (!obj)
      return obj

    var newObj = obj instanceof Array ?  [] : {}
    for (let i in obj){
      if (obj[i] && typeof obj[i] === 'object')
        newObj[i] = Utils.clone(obj[i], ignoreFunctions)
      else if (typeof obj[i] === 'function') {
        if (!ignoreFunctions)
          newObj[i] = obj[i]
      }
      else
        newObj[i] = obj[i]
    }
    return newObj
  }

  // Returns the given string without html markup
  static removeHTML(input){
    let res = input
    if (typeof input === 'string')
      res = input.replace(/\<br\s*\/?\>/gi,' ').replace(/\<[^\>]*\>/g, '').replace(/\s{2,}/g, ' ').trim()

    return res
  }

  static sanitizeProduct(pdt){
    if (!pdt) return pdt

    var props = ['name', 'brand', 'description', 'pkg', 'label', 'listLabel']
    for (let i = 0; i < props.length; i++){
      let prop = props[i]
      if (!pdt[prop]) continue

      pdt[prop] = pdt[prop]
        .replace(/\n/g, '<br>')
        .replace(/{{pbr}}/g, pdt.brand || '')
        .replace(/{{pname}}/g, pdt.name || '')
        .replace(/{{pname2}}/g, pdt.name2 || '')
        .replace(/{{ppckg}}/g, pdt.pkg || '')
        .replace(/{{pdescr}}/g, pdt.description || '')
        .replace(/{{br}}/g, '<br>')
        .replace(/{{strong}}/g, '<strong>')
        .replace(/{{endStrong}}/g, '</strong>')
        .replace(/{{seemore}}/g, '<seemore>')
        .replace(/<\/?script[^>]*>/ig, '') // remove script tags to prevent executing js
    }

    return pdt
  }

  static sanitizeString(input) {
    // remove diacritics
    // replace spaces and special characters not '-' by '_'
    // lowercase
    return input?.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replaceAll(/[^\w-]/gi, '_').toLowerCase();
  }

  // converts dash-notation (kebab-case) string into camelCase
  static toCamelCase(input){
    input = input || ''
    return input.toLowerCase().replace(/-+(.)/g, (match, grp) => { return grp.toUpperCase() }).replace(/-+$/, '')
  }

  // tracking endpoint varies by module and region
  static setTrackingHost(module, region){
    // object that maps module and endpoints. Environment-dependant, set at build time
    const trackHosts = {"BT":"https://trk2-bt.swaven.com","VT":"https://trk2-vt.swaven.com","WTB":"https://trk2-wtb.swaven.com"}

    const host = trackHosts[module]
    return Utils.changeAWSEndpoint(host, region)
  }

  // Shallow comparison of 2 arrays.
  // Returns true if both arrays have the same value for every index
  static arraysEqual(a, b){
    // arrays are considered equals if they're undefined or null.
    if (a == null || b == null)
      return a == b

    if (a.length !== b.length)
      return false

    let isEqual = true
    for (let i = 0; i < a.length; i++){
      if (a[i] !== b[i]){
        isEqual = false
        break
      }
    }
    return isEqual
  }

  // return the number of stores that are inside the provided bounding box
  static countStoresInArea(bbox, stores){
    const minLng = bbox[0],
          minLat = bbox[1],
          maxLng = bbox[2],
          maxLat = bbox[3]

    const count = stores.reduce((count, store) => {
      if (minLat <= store.store.location.latitude && store.store.location.latitude <= maxLat &&
          minLng <= store.store.location.longitude && store.store.location.longitude <= maxLng)
        return count+1
      else
        return count
    }, 0)
    return count
  }

  // Prepends color code with hash sign if it's an RGB color without hash in first place.
  // @param {String} color : value to prepend
  static prependColor(color) {
    if (color && /^([0-9a-f]{3}){1,2}$/i.test(color)) // only when 3 or 6 hex chars
      return '#' + color
    return color
  }

  // sorts 2 strings alphanumerically
  static alphanumericSort(a, b){
    const rx = /(\d+)|([^\d]+)/g, // decompose each value between numbers and non-numbers
          da = a.match(rx),
          db = b.match(rx)

    // compare elems in array
    for (let i = 0; i < da.length; i++){
      const ia = da[i], ib = db[i]

      if (!ib) // a array is longer than b, a comes after b
        return 1

      const na = parseInt(ia, 10),
            nb = parseInt(ib, 10)

      if (isNaN(na) && isNaN(nb)){ // bothe elems are not numbers: compare them alphabetically
        if (ia < ib) return -1
        else if (ia > ib) return 1
        else continue // if elems are equal, check next element in array
      }
      // one is a number and not the other: number comes before
      else if (isNaN(na) && !isNaN(nb))
        return 1
      else if (!isNaN(na) && isNaN(nb))
        return -1
      else { // both are numbers, compare them as numbers
        if (na < nb) return -1
        else if (na > nb) return 1
        else continue
      }
    }
    return 0
  }

  // returns N:
  // N < 0 if A comes before B, 
  // 0 if equal
  // N > 0 if A is after B
  static compareAvails(availA, availB, method, retailerPrio, pinnedRetailers){
    const Aoffline = availA.store.brick || availA.store.collect || availA.store.drive
    const Boffline = availB.store.brick || availB.store.collect || availB.store.drive
    
    // online before offline
    if (Aoffline && !Boffline)
      return 1
    else if (Boffline && !Aoffline)
      return -1
    
    // both offline: return closest one first.
    else if (Aoffline && Boffline)
      return availA.store.distance - availB.store.distance

    // both online: depends on method.
    return Utils.compareOnline(availA, availB, method, retailerPrio, pinnedRetailers)
  }

  static compareOnline(availA, availB, method, retailerPrio, pinnedRetailers){
    // avails from pinned retailers always go on top
    if (pinnedRetailers) {
      const pinIdxA = pinnedRetailers.indexOf(availA.retailer.id)
      const pinIdxB = pinnedRetailers.indexOf(availB.retailer.id)

      if (pinIdxA != -1 && pinIdxB == -1)
        return -1
      else if (pinIdxA == -1 && pinIdxB != -1)
        return 1
      else if (pinIdxA != -1 && pinIdxB != -1) // both are pinned: use order in list
        return pinIdxA - pinIdxB

      // both are non-pinned: continue and use default sort method
    }

    if (method === AVAIL_SORT_ORDER.PRICE)
      return availA.price.amount - availB.price.amount
    else if (method === AVAIL_SORT_ORDER.RANDOM)
      return Math.random() - 0.5 // returns number in the [-0.5, +0.5[ range
    else if (method === AVAIL_SORT_ORDER.PRIO_LIST){
      if (!retailerPrio)
        throw new Error('Missing retailer priority list')

      const iA = retailerPrio.indexOf(availA.retailer.id)
      const iB = retailerPrio.indexOf(availB.retailer.id)

      if (iA === -1 && iB > -1)
        return 1
      else if (iA > -1 && iB === -1)
        return -1
      // same prio (or both outside list): sort by retailer name or price
      else if (iA === iB){
        // same retailer: sort by price, asc.
        if (availA.retailer.name === availB.retailer.name)
          return availA.price.amount - availB.price.amount
        // else sort by retailer name
        else if (availA.retailer.name < availB.retailer.name)
          return -1
        else (availA.retailer.name > availB.retailer.name)
          return 1
      }

      return iA - iB
    }
    else{
      if (availA.retailer.name < availB.retailer.name)
        return -1
      else if (availA.retailer.name > availB.retailer.name)
        return 1
    }
  }

  // Inserts newly-retrieved availabilities in the existing array and returns a new list with the merge result.
  // - Online results come before the offline ones.
  // - Online results are sorted according to the method argument,
  //    i.e. by price, alphabetic (retailer name), retailer priority or random.
  // - Offline results are always sorted by distance.
  //
  // sourceList: list of availabilities to merge into.
  // newAvails: list of newly-retrieved availabilities to merge.
  // method: sort method to apply, cf. AVAIL_SORT_ORDER enum.
  // retailerPrio: ordered list of retailer ids. Used for sorting by retailer priority.
  // pinnedRetailers: ordered list of retailer ids. Used to put pinned retailers first
  static mergeAvailLists(sourceList, newAvails, method, retailerPrio, pinnedRetailers){
    let i = 0, j = 0
    let avails = [...sourceList]

    while(i < avails.length && j < newAvails.length){
      const order = Utils.compareAvails(avails[i], newAvails[j], method, retailerPrio, pinnedRetailers)
      if (order >= 0){
        // if comparison returns 0, item is inserted after current avail,
        // so we add an offset of 1
        const offset = +(order === 0)
        avails.splice(i + offset, 0, newAvails[j])
        j++

        // if item is inserted after, we can skip it and use the following avails item.
        // Since each list is itself sorted, the next newAvail is necessarily after the newly inserted one.
        if (offset) 
          i++
      }

      i++
    }

    // add remaining new avails 
    if (j < newAvails.length){
      avails = [...avails, ...newAvails.slice(j)]
    }

    return avails
  }

  // Loops the provided list, populates differently-filtered avails list and returns them.
  // avails: list of availabilities
  // multiOffer: boolean, whether this mode is enabled. Impacts how avails are deduplicated.
  // offlineGroups: map of grouped offline avails (multiOffer only).
  // availSortOrder: (symbol from AVAIL_SORT_ORDER enum) sort criteria for online avails
  // retailerPriorities: (int array) ordered list of retailers, if that's the desired sort order.
  // pinnedRetailers: ordered list of of pinned retailers, if the option is used
  static sortAvailabilities(avails, multiOffer, offlineGroups, availSortOrder, retailerPriorities, pinnedRetailers){
    // lists to populate
    let offlineAvails = [], typologies = [], onlineAvails = []
      
    // set of used typologies to avoid duplicates
    const usedTypologies = new Set()
    const onlineTypos  = new Set()

    for (let i = 0; i < avails.length; i++){
      const avail = avails[i]

      // Build avail groups for multi-offer. Only for offline avails.
      // Online avails are note deduped for multiOffer.
      if (multiOffer && avail.store.offline){
        // offline avails are deduped by store id,
        const key = avail.store.id

        if (!offlineGroups.has(key))
          offlineGroups.set(key, [avail])
        else
          offlineGroups.get(key).push(avail)
      }
 
      // offline stores are pushed to the realAvails list
      if (avail.store.offline){
        if (!multiOffer)
          // put all offline avails in real list
          offlineAvails.push(avail)
        else {
          // multiOffer: put only the first avail for each store.
          if (offlineGroups.get(avail.store.id)[0] === avail)
            offlineAvails.push(avail)
        }
      }

      // dedup key for by-typo and online&drive lists
      // - multiOffer OFF: API may return results with different retailerProductId for the same product.
      //    Filter by productid instead to remove duplicates.
      // - multiOffer ON: this will retain 1 result per (typology + retailerProductId).
      let typoKey = (avail.store.typologyId || avail.retailer.id) + '/'
      if (!multiOffer)
        typoKey += avail.product.id
      else
        typoKey += avail.product.retailerPdtId

      // build list by typologies, only 1 result per typology
      if (!usedTypologies.has(typoKey)){
        typologies.push(avail)
        usedTypologies.add(typoKey)
      }

      // build online list: online stores, deduplicated by typology
      if (avail.store.online && !onlineTypos.has(typoKey)){
        onlineAvails.push(avail)
        onlineTypos.add(typoKey)
      }
    }

    // list contains online and offline store, each sorted according to different criteria,
    // so sorting again is necessary.
    onlineAvails.sort((a, b) => {
      return Utils.compareOnline(a, b, availSortOrder, retailerPriorities, pinnedRetailers)
    })

    return {
      offline: offlineAvails,
      online: onlineAvails,
      typologies,
    }
  }

  // wrapper to retrieve a performance measure
  static getPerf(name){
    const perf = performance.getEntriesByName(name, 'measure')[0]
    return perf ? Math.round(perf.duration) : -1
  }

  // extract a single param from a url
  static getQueryStringParam(url, name){
    try{
      const arg = decodeURIComponent(
                    url.split('?')[1]
                    .split('&')
                    .filter(x => x.startsWith(name + '='))[0]
                    .substring(name.length +1)
                  )
                  .replace(/\+/g, ' ')
      return arg
    }
    catch(ex){ return ''}
  }

  // convert HEX color to HSL format, and return an object with h, s, l properties.
  static toHSL(hex) {
    // duplicate each RGB component for 3-digits hex values
    if (hex.length < 6)
      hex = hex.replace(/(\w)/g, '$1$1')

    let result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})?$/i.exec(hex)

    let r = parseInt(result[1], 16)
    let g = parseInt(result[2], 16)
    let b = parseInt(result[3], 16)

    // If hex includes opacity, adjust RGB for that opacity before HSL conversion.
    if (!result[4]) {
      const a = parseInt(result[4], 16) / 255
      r = Math.round(r * a + 255 * (1 - a))
      g = Math.round(g * a + 255 * (1 - a))
      b = Math.round(b * a + 255 * (1 - a))
    }

    r /= 255, g /= 255, b /= 255
    let max = Math.max(r, g, b), min = Math.min(r, g, b)
    let h, s, l = (max + min) / 2

    if(max == min){
      h = s = 0 // achromatic
    } else{
      let d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
      switch(max) {
          case r: h = (g - b) / d + (g < b ? 6 : 0); break
          case g: h = (b - r) / d + 2; break
          case b: h = (r - g) / d + 4; break
      }
      h /= 6
    }

    s = s*100
    s = Math.round(s)
    l = l*100
    l = Math.round(l)
    h = Math.round(360*h)
    return {h, s, l}
  }

  // convert a color to HSP value
  // gist taken from https://gist.github.com/krabs-github/ec56e4f1c12cddf86ae9c551aa9d9e04
  // For HSP color space, see http://alienryderflex.com/hsp.html
  static toHSP(color){
    let r, g, b
    if (color.match(/^rgb/)) {
      color = color.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d+(?:\.\d+)?))?\)$/);      
      r = color[1]
      g = color[2]
      b = color[3]
    }
    else {
      color = +("0x" + color.slice(1).replace( 
        color.length < 5 && /(.)/g, '$1$1'
      ));
      r = color >> 16;
      g = color >> 8 & 255;
      b = color & 255;
    }
    const hsp = Math.sqrt(
      0.299 * (r * r) +
      0.587 * (g * g) +
      0.114 * (b * b)
    )
    return hsp
  }

  // returns a lighter shade of the same hue
  static lighten(color, step = 15){
    const value = Utils.toHSL(color)
    value.l += step
    value.s -= Math.round(step/2)
    return `hsl(${value.h} ${value.s}% ${value.l}%)`
  }

  // returns a darker shade of the same hue.
  static darken(color, step = 15){
    const value = Utils.toHSL(color)
    value.l -= step
    value.s += Math.round(step/2)
    return `hsl(${value.h} ${value.s}% ${value.l}%)`
  }

  // Concatenates product name and packaging,
  // unless packaging info is already in the name.
  static labelAndPkg(pdt){
    try {
      if (!pdt)
        return ''

      if (!pdt.pkg)
        return pdt.name

      let lbl = pdt.name
      let units, refUnit

      // go through units map and check if packaing uses any.
      for (let i = 0; i < UNITS.length; i++){
        for (let j = 0; j < UNITS[i].length; j++){
          let unit = UNITS[i][j]
          // look for unit: must be preceded by a word break or digit, and followed by word break.
          const rx = new RegExp(`(?:\\b|\\d)${unit}\\b`, 'i')
          if (rx.test(pdt.pkg)){
            units = UNITS[i]
            refUnit = units[j]
            break
          }
        }
        if (units)
          break
      }

      if (!units){

        const pattern = pdt.pkg
                        .replace(/([\+\*\?\(\)\[\]])/g, '\\$1') // escape special chars (regex operators)
                        // add space pattern after each non-letter|space followed by a letter, or vice-versa
                        // unicode range includes all extended latin, with accented chars.
                        .replace(/([^a-z\s\u00a1-\u024f])(?=[a-z])|([a-z])(?=[^a-z\s\u00a1-\u024f])/gi, '$1$2\\s*')
                        .replace(/((\\s\*)|\s)+/g, '\\s*') // collapse contiguous space & space patterns into a single one
        const rx = new RegExp(pattern, 'i')
        if (!rx.test(pdt.name))
          lbl += (lbl ? ' ' : '') + pdt.pkg
      }
      else{
        // test if name contains packaging OR any variation with an equivalent unit.
        // Add packaging to name only if no match found for any unit.
        const lowerPkg = pdt.pkg.toLowerCase()
        const refUnitRx = new RegExp(`\\s*${refUnit}\\b`)
        const found = units.some(unit => {
          const pattern = lowerPkg.replace(refUnitRx, `\\s*${unit}\\b`)
          const rx = new RegExp(pattern, 'i')
          return rx.test(pdt.name)
        })

        if (!found)
          lbl += (lbl ? ' ' : '') + pdt.pkg
      }

      return lbl
    } catch(ex) {
      console.error(ex)
      return pdt.name;
    }
  }

  // returns a concatenation of store retailer name, store name and full address.
  // Handles cases where retailer and store name are identical or a repetition of one another.
  static getStoreFullname(avail){
    if (!avail)
      return ''

    let elems = []
    if (avail.store.name)
      elems.push(avail.store.name)

    // if retailer name must be added, let it be before store name.
    if (avail.retailer.name && !avail.store.name?.startsWith(avail.retailer.name))
      elems.unshift(avail.retailer.name)

    elems = elems.concat([
      avail.store.location.address,
      avail.store.location.city,
      avail.store.location.zipCode,
    ])

    return elems.join(' ')
  }

  // Parses a font option:
  // - adds to import list if detected as a google font
  // - otherwise parses the name to get the font family and type and add to fontFaces list
  //
  // option: (string) option name, as defined in custom style mapping
  // fontUrl: (string) url to the font file/import
  // fontImports: (array) list of font imports
  // fontFaces: (array) list of font faces
  //
  // fontImport and fontFaces  are lists of {optionName, fontName, rule} objects
  // option Name is used to set the CSS custom variable name
  // fontName is used to set the font-family property
  // rule is the actual content of the import or font-face rule
  static parseFont(option, fontUrl, fontImports, fontFaces) {
    const isImport = fontUrl && fontUrl.includes('fonts.googleapis')

    if (isImport){
      const rule = `@import url('${fontUrl}');`
      const fontName = Utils.getQueryStringParam(fontUrl, 'family')?.split(':')[0]
      fontImports.push({fontName, rule, optionName: option})
    }
    else if (fontUrl){
      const m = fontUrl.match(/(?:[\\\/])([\w-]+)\.(\w+?)$/),
        fontName = m && m[1],
        fontExt = m && m[2]

      const formats = { ttf: 'truetype', otf: 'opentype', eot: 'embedded-opentype' }

      const rule = `@font-face {
        font-family: '${fontName}';
        src: url('${fontUrl}') format('${formats[fontExt] || fontExt}');
        font-weight: normal;
        font-style: normal;
      }`

      fontFaces.push({fontName, rule, optionName: option})
    }
  }

  // declares a css custom variable for a custom font
  // @name: (string) name of the custom variable. Should start with 'font-'
  // @value: (string) font name
  static setFontVariable(name, value){
    // font-default is the root of the font hierarchy, fallback to hardcoded value.
    // if a secondary font is not defined, it will fallback to font-default.
    const fallback = name === 'font-default' ? ', Arial' : ''
    return `--${name}: '${value}'${fallback};\n`
  }

  // returns a label built from the provided pattern.
  // @pdt: product object
  // @pattern: string with 'n' for name, 's' for name2 and 'p' for packaging.
  static buildProductLabel(pdt, pattern){
    let elems = []

    if (!pattern)
      pattern =  'np'

    if (pattern.includes('n'))
      elems.push(pdt.name)
    if (pattern.includes('s'))
      elems.push(pdt.name2)
    if (pattern.includes('p'))
      elems.push(pdt.pkg)

    return elems.join(' ')
  }

  // product comparison function based on the idType
  // @pdt: product object
  // @defaultProductId: default product id to compare with
  // @idType: type of id to compare with. Can be 'gtin', 'mpn', 'mapping_ids', 'product_id' or 'matching_id'.
  static isDefaultProduct(pdt, defaultProductId, idType = 'gtin'){
    if (!defaultProductId)
      return false

    switch (idType) {
      case 'mpn':
        return pdt.mpn && pdt.mpn === defaultProductId
      case 'product_id':
        return pdt.sourcePid && pdt.sourcePid === defaultProductId

      // TODO: missing product fields below from wtb api
      //       need to update mappings.js first
      // case 'mapping_ids':
      //   break;
      // case 'matching_id':
      //   break;

      default:
        // use gtin as default
        return (pdt.ean || pdt.eans)  // can't be the default if there's no ean
          && (pdt.ean === defaultProductId || pdt.eans && pdt.eans.includes(defaultProductId))
    }
  }

  // Normalizes locale keys in an object by converting language codes 
  // from underscore format (e.g., es_PE) to hyphen format (e.g., es-pe) 
  // while preserving other keys and values.
  static normalizeLocaleKeys(obj){
    const regex = /^locale-([a-z]+)_([a-z]+)-(.+)$/i
    const updated = {}
  
    for (const [key, value] of Object.entries(obj)) {
      const newkey = key.replace(regex, (_, lang, country, name) => {
        return `locale-${lang}-${country.toLowerCase()}-${name}`
      })
      updated[newkey] = value
    }
  
    return updated
  }

  // Image event handler to remove loading spinner class and clean up event listeners
  static removeImgLoadingHandler(event) {
    const img = event.currentTarget
    
    // Find parent element with 'wl' class starting from the image
    let parent = img.parentElement
    while (parent && !parent.classList.contains('wl')) {
      parent = parent.parentElement
    }
    parent?.classList.remove('loading')
    
    // Remove both event listeners to prevent memory leaks
    img.removeEventListener('load', Utils.removeImgLoadingHandler)
    img.removeEventListener('error', Utils.removeImgLoadingHandler)
  }

  // Builds the URl for the products request, whether standard or paginated.
  static buildProductsRequestUrl(
    wtbid,
    locale,
    country,
    availPrdOnly,
    pids,
    location,
    forceLoc,
    draft,
    idType,
    offset,
    limit,
    catalogFilters,
    tracker
  ){
    let url = `https://wtb-api-hub.swaven.com/wtb/v2/api/search/products/${pids || ''}?wtbid=${wtbid}&lang=${locale}&av=${availPrdOnly}&country=${country}`

    // dont't include location if:
    // - it's empty
    // - it's the default location
    // - we force using API location
    if (!forceLoc && !location.isEmpty && !location.isDefault)
      url += `&lat=${location.latitude}&lng=${location.longitude}&addr=${location.address}&city=${location.city}&zc=${location.zipCode}&cc=${location.country}&placetype=${location.type}&state=${location.region}`

    if (draft)
      url += '&draft=1'

    if (idType)
      url += `&idtype=${idType}`

    if (offset != null)
      url += `&offset=${offset}`

    if (limit != null)
      url += `&limit=${limit}`

    if (catalogFilters)
      url += `&filters={${catalogFilters}}`

    // Add Native Experiences parameters
    if (window.name)
      url += `&instanceId=${encodeURIComponent(window.name)}`
    if (tracker?.rfr2)
      url += `&currentUrl=${encodeURIComponent(tracker.rfr2)}`
    // Only add sessionUrl if it's different from currentUrl
    if (tracker?.session?.url && tracker.session.url !== tracker.rfr2)
      url += `&sessionUrl=${encodeURIComponent(tracker.session.url)}`

    return encodeURI(url)
  }

  // COT: empty → ''; known id → itself; unknown → 'other'
  static normalizeCot(cot) {
    if (!cot) return ''
    return COT_KNOWN.includes(cot) ? cot : 'other'
  }

  // COT → awe-font class. barIcon overrides COT_ICON_MAP.bar (glass vs beer).
  static cotIconGlyph(cot, barIcon = 'glass') {
    const glyph = cot === 'bar' ? (barIcon || COT_ICON_MAP.bar) : COT_ICON_MAP[cot]
    return glyph ? 'icon-' + glyph : ''
  }

  // Resolve icon/label flags for store badge + filter chips.
  // Returns null when cot missing (no VIP scot → no badge).
  static resolveCotDisplay(cot, { simpleView = false, barIcon = 'glass' } = {}) {
    const normalized = Utils.normalizeCot(cot)
    if (!normalized) return null

    let displayCot = normalized
    let labelKey = 'COT_BADGE_LABEL_' + normalized.toUpperCase()

    if (simpleView) {
      const onPrem = this.COT_ON_PREMISE.includes(normalized)
      if (onPrem) {
        displayCot = 'bar'
        labelKey = 'COT_BADGE_LABEL_ON_PREMISE'
      } else {
        displayCot = 'store'
        labelKey = 'COT_BADGE_LABEL_OFF_PREMISE'
      }
    }

    return {
      iconClass: Utils.cotIconGlyph(displayCot, barIcon),
      labelKey,
    }
  }
}
